import {
  defaultMetas,
  type Faro,
  initializeFaro,
  MetaAttributes,
} from '@grafana/faro-web-sdk';
import { type Environment, getClientAddress, getCookie } from 'common-utils';
import { assign, createMachine } from 'xstate';

declare global {
  interface Window {
    faro: Faro;
  }
}
export type FaroMetaAttributes = MetaAttributes;
export type RumMethods = Pick<
  Faro['api'],
  'pushError' | 'pushMeasurement' | 'getSession' | 'setUser' | 'setSession'
>;

export type RumAppType =
  | 'paperpal-edit-for-web'
  | 'paperpal-edit-for-word'
  | 'paperpal-preflight'
  | 'paperpal-preflight-admin'
  | 'paperpal-editorial-manager'
  | 'paperpal-edit-for-extension'
  | 'paperpal-edit-for-google-doc'
  | 'paperpal-lifesciences-for-web'
  | 'discovery';

export function createRumMachine(config: {
  grafana: { host: string; key: string };
  app: RumAppType;
  version: string;
  env: Environment;
  isRegionEnabled?: boolean;
}) {
  const machine = createMachine(
    {
      id: 'rum machine',
      predictableActionArguments: true,
      preserveActionOrder: true,
      schema: {
        context: {} as { methods?: RumMethods },
        events: {} as { type: 'faro is initialized' },
      },
      tsTypes: {} as import('./rum-machine.typegen').Typegen0,
      context: {},
      initial: 'on the server',
      states: {
        'on the server': {
          always: {
            cond: 'window is defined',
            target: 'on the client',
          },
        },
        'on the client': {
          always: [
            {
              cond: 'is region enabled', // 👈 check first
              target: 'region disabled',
            },
            {
              cond: 'faro is not defined',
              target: 'setting up faro',
            },
            {
              target: 'faro is ready',
            },
          ],
        },
        'region disabled': {
          entry: ['set noop methods'],
          type: 'final',
        },
        'setting up faro': {
          entry: ['initialize faro'],
          invoke: {
            src: 'faro listener',
          },
          on: {
            'faro is initialized': {
              target: 'faro is ready',
            },
          },
        },
        'faro is ready': {
          entry: ['update methods'],
          type: 'final',
        },
      },
    },
    {
      actions: {
        'initialize faro': () => {
          const clientAddress = getClientAddress();
          const bigIntId = getCookie('__ivc');
          initializeFaro({
            url: config.grafana.host,
            apiKey: config.grafana.key,
            app: {
              name: config.app,
              version: config.version,
              environment: config.env,
            },

            batching: {
              enabled: true, // default is true
              itemLimit: 100, // flush when 100 events are buffered default is 50,
              sendTimeout: 60000, // or after 60 seconds (whichever comes first) default is 250ms
            },
            beforeSend: (event) => {
              try {
                //Check if its a log signal
                if (event.type === 'log' && 'level' in event.payload) {
                  const level = event.payload.level;
                  const message = event.payload?.message || '';

                  // Drop only specific warnings
                  if (
                    level === 'warn' &&
                    message.includes('Missing Translation')
                  ) {
                    return null; // 🚫 Do NOT send this event
                  }
                } else if (
                  event.type === 'exception' && //Check if its a exception signal
                  'level' in event.payload
                ) {
                  const level = event.payload.level;
                  const message = event.payload?.message || '';

                  // Drop only specific warnings
                  if (
                    level === 'error' &&
                    /Position \d+ outside of fragment/.test(message) // Will skip errors like "Position 3 outside of fragment"
                  ) {
                    return null; // 🚫 Do NOT send this event
                  }
                }
                return event; // ✅ Allow all other events
              } catch (error) {
                // If any error occurs during filtering, log it and proceed to send the event
                console.error(
                  'Rum Machine error: Error in beforeSend filtering:',
                  JSON.stringify(error),
                );
                return event; // ✅ Allow all other events
              }
            },
            user: {
              attributes: {
                ip: clientAddress?.ip ?? '',
                bigIntId,
              },
            },
            metas: [...defaultMetas],
          });
        },
        'set noop methods': assign({
          methods: () => ({
            pushError: () => {},
            pushMeasurement: () => {},
            getSession: () => ({}),
            setSession: () => ({}),
            setUser: () => ({}),
          }),
        }),
        'update methods': assign({
          methods: () => {
            return {
              pushError: window.faro.api.pushError,
              pushMeasurement: window.faro.api.pushMeasurement,
              getSession: window.faro.api.getSession,
              setSession: window.faro.api.setSession,
              setUser: window.faro.api.setUser,
            };
          },
        }),
      },
      guards: {
        'window is defined': () => {
          return typeof window !== 'undefined';
        },
        'faro is not defined': () => {
          return !window['faro'];
        },
        'is region enabled': () => !!config.isRegionEnabled,
      },
      services: {
        'faro listener': () => (send) => {
          const interval = setInterval(() => {
            if (window['faro']) {
              send('faro is initialized');
            }
          }, 1000);

          return () => {
            clearInterval(interval);
          };
        },
      },
    },
  );

  return machine;
}
